import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  type Ref,
} from 'react'
import { Link } from '@tanstack/react-router'
import { StaffAvatar } from '@/components/staff-avatar'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { LoadingButton } from '@/components/loading-button'
import {
  useProjectComments,
  useCreateComment,
  useUpdateComment,
  useDeleteComment,
  useMentionSearch,
} from '@/api/hooks'
import { useAuth } from '@/hooks/use-auth'
import { parseUTCDate } from '@/lib/utils'
import {
  MessageSquare,
  Send,
  Pencil,
  Trash2,
  X,
  Check,
  Loader2,
  Package,
  FileText,
  FolderOpen,
  Users,
  UserCog,
  Factory,
  ListChecks,
  ReceiptText,
} from 'lucide-react'
import type { components } from '@/api/schema'
import { withContextTags, type NewComment } from '@/components/comment-tags'

type CommentRead = components['schemas']['ProjectCommentRead']
/** What any comment thread shows: project comments and manufacturer rating comments (USA-1272). */
export type ThreadComment = Pick<
  CommentRead,
  'id' | 'uuid' | 'user' | 'content' | 'mentions' | 'created_at' | 'updated_at'
>
export type MentionResult = components['schemas']['MentionSearchResult']
type CommentMention = {
  entity_type: string
  entity_uuid: string
  display_text: string
}

export type CommentContextMention = CommentMention

// ---- Entity type config for mention badges and routing ----

const ENTITY_CONFIG: Record<
  string,
  {
    icon: typeof Package
    label: string
    color: string
    bg: string
    toRoute: string
    paramKey: string
  }
> = {
  order: {
    icon: Package,
    label: 'Order',
    color: 'text-blue-700',
    bg: 'bg-blue-100',
    toRoute: '/orders/$orderId',
    paramKey: 'orderId',
  },
  purchase_order: {
    icon: FileText,
    label: 'PO',
    color: 'text-emerald-700',
    bg: 'bg-emerald-100',
    toRoute: '/purchase-orders/$poId',
    paramKey: 'poId',
  },
  project: {
    icon: FolderOpen,
    label: 'Project',
    color: 'text-violet-700',
    bg: 'bg-violet-100',
    toRoute: '/projects/$projectId',
    paramKey: 'projectId',
  },
  customer_group: {
    icon: Users,
    label: 'Customer',
    color: 'text-amber-700',
    bg: 'bg-amber-100',
    toRoute: '/customers/$groupId',
    paramKey: 'groupId',
  },
  manufacturer: {
    icon: Factory,
    label: 'Manufacturer',
    color: 'text-pink-700',
    bg: 'bg-pink-100',
    toRoute: '/manufacturers/$manuId',
    paramKey: 'manuId',
  },
  ap_invoice: {
    icon: ReceiptText,
    label: 'Invoice',
    color: 'text-orange-800',
    bg: 'bg-orange-100',
    toRoute: '/invoices/$invoiceId',
    paramKey: 'invoiceId',
  },
  staff: {
    icon: UserCog,
    label: 'Staff',
    color: 'text-cyan-700',
    bg: 'bg-cyan-100',
    toRoute: '/staff/$staffId',
    paramKey: 'staffId',
  },
}

// ---- MentionText: renders @[display](type:uuid) as clickable badges ----

const MENTION_REGEX = /@\[([^\]]+)\]\((\w+):([^)]+)\)/g

/** A comment left on an action item (USA-1443) carries it, and its PO, as tags. */
const ACTION_ITEM_ENTITY = 'action_item'
/** A note left under an AP invoice, or on its action items, carries the invoice as a tag (USA-1180). */
const AP_INVOICE_ENTITY = 'ap_invoice'
/** Tags a comment keeps when it is edited: what it was left on. */
const CONTEXT_ENTITY_TYPES = new Set(['purchase_order', ACTION_ITEM_ENTITY, AP_INVOICE_ENTITY])

/** Where an action item tag opens: its invoice's page, else its PO's, else its project's, scrolled to the item. */
function ActionItemMention({
  actionUuid,
  displayText,
  invoiceUuid,
  poUuid,
  projectUuid,
}: {
  actionUuid: string
  displayText: string
  invoiceUuid?: string
  poUuid?: string
  projectUuid?: string
}) {
  const className =
    'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-sky-100 text-sky-800 hover:opacity-80 transition-opacity'
  const content = (
    <>
      <ListChecks className="h-3 w-3" aria-hidden />
      <span className="sr-only">Action item: </span>
      {displayText}
    </>
  )
  if (invoiceUuid) {
    return (
      <Link to="/invoices/$invoiceId" params={{ invoiceId: invoiceUuid }} search={{ action: actionUuid }} className={className}>
        {content}
      </Link>
    )
  }
  if (poUuid) {
    return (
      <Link to="/purchase-orders/$poId" params={{ poId: poUuid }} search={{ action: actionUuid }} className={className}>
        {content}
      </Link>
    )
  }
  if (projectUuid) {
    return (
      <Link to="/projects/$projectId" params={{ projectId: projectUuid }} search={{ action: actionUuid }} className={className}>
        {content}
      </Link>
    )
  }
  return <span className={className}>{content}</span>
}

export function MentionText({
  content,
  mentions,
  projectUuid,
  omitTypes,
}: {
  content: string
  /** The comment's tags, so an action item tag can open the PO it belongs to. */
  mentions?: readonly CommentMention[]
  projectUuid?: string
  /** Tags not to show, e.g. the action item and PO a card's own comments were left on. */
  omitTypes?: ReadonlySet<string>
}) {
  const parts: React.ReactNode[] = []
  const poUuid = mentions?.find((mention) => mention.entity_type === 'purchase_order')?.entity_uuid
  const invoiceUuid = mentions?.find((mention) => mention.entity_type === AP_INVOICE_ENTITY)?.entity_uuid
  let lastIndex = 0
  let match: RegExpExecArray | null

  const regex = new RegExp(MENTION_REGEX.source, 'g')
  while ((match = regex.exec(content)) !== null) {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index))
    }

    const displayText = match[1] ?? ''
    const entityType = match[2] ?? ''
    const entityUuid = match[3] ?? ''
    const config = ENTITY_CONFIG[entityType]

    if (omitTypes?.has(entityType)) {
      // Drop the tag and the space after it.
      if (content[match.index + match[0].length] === ' ') regex.lastIndex += 1
    } else if (entityType === ACTION_ITEM_ENTITY) {
      parts.push(
        <ActionItemMention
          key={`${entityUuid}-${match.index}`}
          actionUuid={entityUuid}
          displayText={displayText}
          invoiceUuid={invoiceUuid}
          poUuid={poUuid}
          projectUuid={projectUuid}
        />,
      )
    } else if (config) {
      const Icon = config.icon
      parts.push(
        <Link
          key={`${entityUuid}-${match.index}`}
          to={config.toRoute}
          params={{ [config.paramKey]: entityUuid }}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${config.bg} ${config.color} hover:opacity-80 transition-opacity`}
        >
          <Icon className="h-3 w-3" />
          {displayText}
        </Link>,
      )
    } else {
      parts.push(
        <span key={match.index} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
          {displayText}
        </span>,
      )
    }

    lastIndex = regex.lastIndex
  }

  // Add remaining text
  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex))
  }

  return <span className="whitespace-pre-wrap">{parts}</span>
}

// ---- Relative time formatting ----

function formatRelativeTime(dateStr: string): string {
  const date = parseUTCDate(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

// ---- MentionDropdown ----

function MentionDropdown({
  results,
  isLoading,
  selectedIndex,
  onSelect,
}: {
  results: MentionResult[]
  isLoading: boolean
  selectedIndex: number
  onSelect: (result: MentionResult) => void
}) {
  if (results.length === 0 && !isLoading) {
    return (
      <div className="absolute z-50 mt-1 w-72 rounded-md border bg-white shadow-lg p-2">
        <p className="text-sm text-muted-foreground px-2 py-1">No results found</p>
      </div>
    )
  }

  return (
    <div className="absolute z-50 mt-1 w-80 max-h-64 overflow-y-auto rounded-md border bg-white shadow-lg">
      {isLoading && results.length === 0 && (
        <div className="flex items-center gap-2 px-3 py-2">
          <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" />
          <span className="text-sm text-muted-foreground">Searching...</span>
        </div>
      )}
      {isLoading && results.length > 0 && (
        <div className="flex justify-center py-1">
          <div className="h-3 w-3 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground" />
        </div>
      )}
      {results.map((result, idx) => {
        const config = ENTITY_CONFIG[result.entity_type]
        const Icon = config?.icon ?? Package
        return (
          <button
            key={`${result.entity_type}-${result.entity_uuid}`}
            type="button"
            className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-100 ${
              idx === selectedIndex ? 'bg-gray-100' : ''
            }`}
            onMouseDown={(e) => {
              e.preventDefault()
              onSelect(result)
            }}
          >
            <span
              className={`flex h-6 w-6 items-center justify-center rounded ${config?.bg ?? 'bg-gray-100'}`}
            >
              <Icon className={`h-3.5 w-3.5 ${config?.color ?? 'text-gray-600'}`} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-medium truncate">{result.display_text}</span>
                {result.in_project && (
                  <span className="text-[10px] text-emerald-600 font-medium shrink-0">
                    this project
                  </span>
                )}
              </div>
              {result.secondary_text && (
                <p className="text-xs text-muted-foreground truncate">{result.secondary_text}</p>
              )}
            </div>
            <span className="text-[10px] text-muted-foreground shrink-0">
              {config?.label ?? result.entity_type}
            </span>
          </button>
        )
      })}
    </div>
  )
}

// ---- Rich mention input helpers ----

type TextSegment = { kind: 'text'; text: string }
type MentionSegment = { kind: 'mention'; display: string; entityType: string; entityUuid: string }
type Segment = TextSegment | MentionSegment

function parseContentToSegments(content: string): Segment[] {
  const segments: Segment[] = []
  const regex = new RegExp(MENTION_REGEX.source, 'g')
  let lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ kind: 'text', text: content.slice(lastIndex, match.index) })
    }
    segments.push({
      kind: 'mention',
      display: match[1] ?? '',
      entityType: match[2] ?? '',
      entityUuid: match[3] ?? '',
    })
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < content.length) {
    segments.push({ kind: 'text', text: content.slice(lastIndex) })
  }
  return segments
}

function createMentionSpan(entityType: string, entityUuid: string, displayText: string): HTMLSpanElement {
  const span = document.createElement('span')
  span.contentEditable = 'false'
  span.dataset.entityType = entityType
  span.dataset.entityUuid = entityUuid
  span.dataset.mentionDisplay = displayText
  const config = ENTITY_CONFIG[entityType]
  const bg = config?.bg ?? 'bg-gray-100'
  const color = config?.color ?? 'text-gray-700'
  span.className = `inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${bg} ${color} mx-0.5 select-none cursor-default`
  span.textContent = displayText
  return span
}

/** Ensures the editor has a zero-width space after any trailing non-editable element so the cursor can be placed there */
function ensureTrailingCursor(el: HTMLElement) {
  const last = el.lastChild
  if (!last) return
  // If the last node is a mention span (or any element that isn't a text node), add a ZWS text node
  if (last.nodeType === Node.ELEMENT_NODE && (last as HTMLElement).tagName !== 'BR') {
    el.appendChild(document.createTextNode('\u200B'))
  }
}

function populateEditor(el: HTMLElement, content: string) {
  el.innerHTML = ''
  const segments = parseContentToSegments(content)
  for (const seg of segments) {
    if (seg.kind === 'text') {
      // Split by newlines to insert <br> elements
      const lines = seg.text.split('\n')
      for (let i = 0; i < lines.length; i++) {
        if (i > 0) el.appendChild(document.createElement('br'))
        const line = lines[i]!
        if (line.length > 0) el.appendChild(document.createTextNode(line))
      }
    } else {
      el.appendChild(createMentionSpan(seg.entityType, seg.entityUuid, seg.display))
    }
  }
  ensureTrailingCursor(el)
}

function serializeEditorContent(el: HTMLElement): {
  content: string
  mentions: Array<{ entity_type: string; entity_uuid: string; display_text: string }>
} {
  let content = ''
  const mentions: Array<{ entity_type: string; entity_uuid: string; display_text: string }> = []

  function walk(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      content += node.textContent ?? ''
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const htmlEl = node as HTMLElement
      // Mention span
      if (htmlEl.dataset.entityType && htmlEl.dataset.entityUuid) {
        const entityType = htmlEl.dataset.entityType
        const entityUuid = htmlEl.dataset.entityUuid
        const displayText = htmlEl.dataset.mentionDisplay ?? htmlEl.textContent ?? ''
        content += `@[${displayText}](${entityType}:${entityUuid})`
        mentions.push({ entity_type: entityType, entity_uuid: entityUuid, display_text: displayText })
        return // don't walk children of mention span
      }
      // BR or block-level element → newline
      const tag = htmlEl.tagName
      if (tag === 'BR') {
        content += '\n'
        return
      }
      const isBlock = tag === 'DIV' || tag === 'P' || tag === 'BLOCKQUOTE'
      if (isBlock && content.length > 0 && !content.endsWith('\n')) {
        content += '\n'
      }
      for (const child of Array.from(node.childNodes)) {
        walk(child)
      }
      if (isBlock && content.length > 0 && !content.endsWith('\n')) {
        content += '\n'
      }
    }
  }

  for (const child of Array.from(el.childNodes)) {
    walk(child)
  }
  // Strip zero-width spaces (used for cursor positioning) and trailing newlines
  content = content.replace(/\u200B/g, '').replace(/\n$/, '')
  return { content, mentions }
}

/** Gets the plain text from editor start to the current cursor position */
function getTextBeforeCursor(editor: HTMLElement): string | null {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return null
  try {
    const range = sel.getRangeAt(0)
    const preRange = document.createRange()
    preRange.selectNodeContents(editor)
    preRange.setEnd(range.startContainer, range.startOffset)
    // Walk the range to build text (accounting for mention spans and BRs)
    const fragment = preRange.cloneContents()
    const temp = document.createElement('div')
    temp.appendChild(fragment)
    // Replace mention spans with their display text, BRs with newlines
    let text = ''
    function walkFragment(node: Node) {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent ?? ''
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement
        if (el.dataset.entityType) {
          text += el.textContent ?? ''
          return
        }
        if (el.tagName === 'BR') {
          text += '\n'
          return
        }
        for (const child of Array.from(node.childNodes)) {
          walkFragment(child)
        }
      }
    }
    walkFragment(temp)
    return text
  } catch {
    return null
  }
}

// ---- MentionInput ----

export interface MentionInputHandle {
  getContent: () => {
    content: string
    mentions: Array<{ entity_type: string; entity_uuid: string; display_text: string }>
  }
  clear: () => void
  focus: () => void
}

export function MentionInput({
  initialValue,
  projectUuid,
  placeholder,
  disabled,
  autoFocus,
  className,
  onSubmit,
  onChange,
  onMentionSelect,
  ref,
}: {
  initialValue?: string
  projectUuid?: string
  placeholder?: string
  disabled?: boolean
  autoFocus?: boolean
  className?: string
  onSubmit?: () => void
  onChange?: (content: string) => void
  onMentionSelect?: (result: MentionResult) => void
  ref?: Ref<MentionInputHandle>
}) {
  const editorRef = useRef<HTMLDivElement>(null)
  const [isEmpty, setIsEmpty] = useState(!initialValue)
  const [mentionQuery, setMentionQuery] = useState('')
  const [showMention, setShowMention] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  // Store the text node + offset where @ was typed, for insertion
  const mentionAnchorRef = useRef<{ node: Text; offset: number } | null>(null)
  // Delayed close on blur so a dropdown click lands first; cancelled on unmount
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelBlurTimer = useCallback(() => clearTimeout(blurTimerRef.current ?? undefined), [])

  useEffect(() => cancelBlurTimer, [cancelBlurTimer])

  const { data: mentionResults = [], isLoading: mentionLoading } = useMentionSearch(
    mentionQuery,
    projectUuid,
  )

  useImperativeHandle(ref, () => ({
    getContent: () => {
      const editor = editorRef.current
      if (!editor) return { content: '', mentions: [] }
      return serializeEditorContent(editor)
    },
    clear: () => {
      const editor = editorRef.current
      if (!editor) return
      editor.innerHTML = ''
      setIsEmpty(true)
      closeMention()
    },
    focus: () => editorRef.current?.focus(),
  }))

  // Populate editor on mount / initialValue change
  useEffect(() => {
    const editor = editorRef.current
    if (!editor) return
    if (initialValue) {
      populateEditor(editor, initialValue)
      setIsEmpty(false)
    }
    if (autoFocus) {
      editor.focus()
      // Move cursor to end
      const sel = window.getSelection()
      if (sel) {
        sel.selectAllChildren(editor)
        sel.collapseToEnd()
      }
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const closeMention = useCallback(() => {
    setShowMention(false)
    setMentionQuery('')
    setSelectedIndex(0)
    mentionAnchorRef.current = null
  }, [])

  const checkForMentionTrigger = useCallback(() => {
    const editor = editorRef.current
    if (!editor) return
    const text = getTextBeforeCursor(editor)
    if (text === null) {
      closeMention()
      return
    }
    // Find last @ that isn't preceded by a word character
    const atMatch = text.match(/(?:^|[^\w])@([^\s@]*)$/)
    if (atMatch) {
      const query = atMatch[1] ?? ''
      setShowMention(true)
      setMentionQuery(query)
      setSelectedIndex(0)
    } else {
      if (showMention) closeMention()
    }
  }, [showMention, closeMention])

  const updateEmpty = useCallback(() => {
    const editor = editorRef.current
    if (!editor) return
    setIsEmpty((editor.textContent ?? '').trim().length === 0)
  }, [])

  const insertMention = useCallback(
    (result: MentionResult) => {
      const editor = editorRef.current
      if (!editor) return
      const sel = window.getSelection()
      if (!sel || sel.rangeCount === 0) return

      const range = sel.getRangeAt(0)
      const cursorNode = range.startContainer
      const cursorOffset = range.startOffset

      // We need to find the @ in the text before cursor and remove @query
      // Walk backwards to find it
      if (cursorNode.nodeType === Node.TEXT_NODE) {
        const textNode = cursorNode as Text
        const textBefore = textNode.textContent?.slice(0, cursorOffset) ?? ''
        // Find the last @ that triggered the mention
        const atIdx = textBefore.lastIndexOf('@')
        if (atIdx === -1) {
          closeMention()
          return
        }

        const beforeAt = textBefore.slice(0, atIdx)
        const afterCursor = textNode.textContent?.slice(cursorOffset) ?? ''

        // Create the mention span
        const mentionSpan = createMentionSpan(
          result.entity_type,
          result.entity_uuid,
          result.display_text,
        )
        // Create a trailing space text node
        const spaceNode = document.createTextNode('\u00A0')

        // Replace the text node with: beforeAt text + mention span + space + afterCursor text
        const parent = textNode.parentNode!
        if (beforeAt.length > 0) {
          parent.insertBefore(document.createTextNode(beforeAt), textNode)
        }
        parent.insertBefore(mentionSpan, textNode)
        parent.insertBefore(spaceNode, textNode)
        if (afterCursor.length > 0) {
          textNode.textContent = afterCursor
        } else {
          parent.removeChild(textNode)
        }

        // Set cursor after the space
        const newRange = document.createRange()
        newRange.setStartAfter(spaceNode)
        newRange.collapse(true)
        sel.removeAllRanges()
        sel.addRange(newRange)
      }

      ensureTrailingCursor(editor)
      closeMention()
      updateEmpty()
      onChange?.(serializeEditorContent(editor).content)
      onMentionSelect?.(result)
      editor.focus()
    },
    [closeMention, onChange, onMentionSelect, updateEmpty],
  )

  const handleInput = useCallback(() => {
    const editor = editorRef.current
    if (editor) ensureTrailingCursor(editor)
    updateEmpty()
    checkForMentionTrigger()
    if (editor) onChange?.(serializeEditorContent(editor).content)
  }, [updateEmpty, checkForMentionTrigger, onChange])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (showMention && mentionResults.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          setSelectedIndex((prev) => Math.min(prev + 1, mentionResults.length - 1))
          return
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setSelectedIndex((prev) => Math.max(prev - 1, 0))
          return
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault()
          insertMention(mentionResults[selectedIndex]!)
          return
        }
        if (e.key === 'Escape') {
          e.preventDefault()
          closeMention()
          return
        }
      }

      // Ctrl+Enter to submit
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        onSubmit?.()
      }
    },
    [showMention, mentionResults, selectedIndex, insertMention, closeMention, onSubmit],
  )

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text/plain')
    document.execCommand('insertText', false, text)
  }, [])

  return (
    <div className="relative">
      {isEmpty && (
        <div className="pointer-events-none absolute left-3 top-2 text-sm text-muted-foreground">
          {placeholder}
        </div>
      )}
      <div
        ref={editorRef}
        contentEditable={!disabled}
        role="textbox"
        aria-label={placeholder}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onBlur={() => {
          cancelBlurTimer()
          blurTimerRef.current = setTimeout(closeMention, 200)
        }}
        className={`border-input focus-visible:border-ring focus-visible:ring-ring/50 rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 min-h-[80px] w-full whitespace-pre-wrap break-words ${disabled ? 'cursor-not-allowed opacity-50' : ''} ${className ?? ''}`}
      />
      {showMention && (
        <MentionDropdown
          results={mentionResults}
          isLoading={mentionLoading}
          selectedIndex={selectedIndex}
          onSelect={insertMention}
        />
      )}
    </div>
  )
}

// ---- CommentComposer: the input + send button, for any thread ----

export function CommentComposer({
  onSubmit,
  pending,
  projectUuid,
  placeholder = 'Add a comment... Use @ to mention entities',
}: {
  /** Called with the comment; call `clear` once it is saved. */
  onSubmit: (comment: NewComment, clear: () => void) => void
  pending: boolean
  projectUuid?: string
  placeholder?: string
}) {
  const inputRef = useRef<MentionInputHandle>(null)

  const handleSubmit = useCallback(() => {
    const result = inputRef.current?.getContent()
    if (!result || !result.content.trim()) return
    onSubmit(
      { content: result.content.trim(), mentions: result.mentions },
      () => inputRef.current?.clear(),
    )
  }, [onSubmit])

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
      <div className="flex-1">
        <MentionInput
          ref={inputRef}
          projectUuid={projectUuid}
          placeholder={placeholder}
          disabled={pending}
          onSubmit={handleSubmit}
        />
      </div>
      <LoadingButton
        size="sm"
        aria-label="Add comment"
        onClick={handleSubmit}
        loading={pending}
        className="self-end"
      >
        <Send className="h-4 w-4" />
      </LoadingButton>
    </div>
  )
}

// ---- CommentForm ----

export function CommentForm({
  projectUuid,
  contextMention,
  contextMentions,
  placeholder,
  onPosted,
}: {
  projectUuid: string
  contextMention?: CommentContextMention
  /** What the comment is left on, in order (e.g. a PO and its action item); each becomes a tag. */
  contextMentions?: readonly CommentContextMention[]
  placeholder?: string
  onPosted?: () => void
}) {
  const createComment = useCreateComment(projectUuid)

  const handleSubmit = useCallback(
    (typed: NewComment, clear: () => void) => {
      const context = contextMentions ?? (contextMention ? [contextMention] : [])
      createComment.mutate(
        withContextTags(typed, context),
        {
          onSuccess: () => {
            clear()
            onPosted?.()
          },
        },
      )
    },
    [contextMention, contextMentions, createComment, onPosted],
  )

  return (
    <CommentComposer
      projectUuid={projectUuid}
      onSubmit={handleSubmit}
      pending={createComment.isPending}
      placeholder={placeholder}
    />
  )
}

// ---- CommentItem: one comment, with edit/delete for its author ----

export function CommentItem({
  comment,
  currentUserEmail,
  projectUuid,
  saving,
  deleting,
  onSave,
  onDelete,
  omitTypes,
}: {
  comment: ThreadComment
  currentUserEmail: string | undefined
  projectUuid?: string
  /** Tags not to show, e.g. on an action item card, the item and PO every comment there was left on. */
  omitTypes?: ReadonlySet<string>
  saving: boolean
  deleting: boolean
  /** Called with the edited text; call `done` once it is saved. */
  onSave: (content: string, done: () => void) => void
  onDelete: () => void
}) {
  const [editing, setEditing] = useState(false)
  const editInputRef = useRef<MentionInputHandle>(null)

  const isOwn = currentUserEmail === comment.user.email
  const isEdited =
    new Date(comment.updated_at).getTime() - new Date(comment.created_at).getTime() > 1000

  const handleSaveEdit = useCallback(() => {
    const result = editInputRef.current?.getContent()
    if (!result || !result.content.trim()) return
    // Keep the tags for what the comment was left on (its PO, its action item) in front of the text.
    const contextMarkup = comment.mentions
      .filter((mention) => CONTEXT_ENTITY_TYPES.has(mention.entity_type))
      .map((mention) => `@[${mention.display_text}](${mention.entity_type}:${mention.entity_uuid})`)
      .filter((markup) => !result.content.includes(markup))
      .join(' ')
    const content = contextMarkup ? `${contextMarkup} ${result.content.trim()}` : result.content.trim()
    onSave(content, () => setEditing(false))
  }, [comment.mentions, onSave])

  const isBusy = deleting || saving

  return (
    <div className={`flex gap-3 group ${deleting ? 'opacity-50' : ''}`}>
      <StaffAvatar
        name={comment.user.name}
        photoUrl={comment.user.photo_url}
        className="h-8 w-8 shrink-0 mt-0.5"
        fallbackClassName="text-xs bg-primary/10 text-primary"
      >
        {getInitials(comment.user.name)}
      </StaffAvatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{comment.user.name}</span>
          <span className="text-xs text-muted-foreground">
            {formatRelativeTime(comment.created_at)}
          </span>
          {isEdited && <span className="text-xs text-muted-foreground italic">(edited)</span>}
          {isOwn && !editing && (
            // Shown on hover; always shown on touch screens, which have none.
            <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity flex gap-1 ml-auto">
              <button
                type="button"
                aria-label="Edit comment"
                onClick={() => setEditing(true)}
                className="p-1 text-muted-foreground hover:text-foreground rounded"
                disabled={isBusy}
              >
                <Pencil className="h-3 w-3" />
              </button>
              <button
                type="button"
                aria-label="Delete comment"
                onClick={onDelete}
                className="p-1 text-muted-foreground hover:text-destructive rounded"
                disabled={isBusy}
              >
                {deleting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Trash2 className="h-3 w-3" />
                )}
              </button>
            </div>
          )}
        </div>
        {editing ? (
          <div className="mt-1">
            <MentionInput
              ref={editInputRef}
              initialValue={comment.content}
              projectUuid={projectUuid}
              autoFocus
              disabled={saving}
              className="min-h-[60px] text-sm"
              onSubmit={handleSaveEdit}
            />
            <div className="flex gap-1 mt-1">
              <LoadingButton
                size="sm"
                variant="ghost"
                onClick={handleSaveEdit}
                loading={saving}
                loadingText="Saving..."
              >
                <Check className="h-3 w-3 mr-1" />
                Save
              </LoadingButton>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                <X className="h-3 w-3 mr-1" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-0.5 text-sm">
            <MentionText
              content={comment.content}
              mentions={comment.mentions}
              projectUuid={projectUuid}
              omitTypes={omitTypes}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// ---- SingleComment ----

export function SingleComment({
  comment,
  projectUuid,
  currentUserEmail,
  omitTypes,
}: {
  comment: CommentRead
  projectUuid: string
  currentUserEmail: string | undefined
  omitTypes?: ReadonlySet<string>
}) {
  const updateComment = useUpdateComment(projectUuid)
  const deleteComment = useDeleteComment(projectUuid)
  return (
    <CommentItem
      comment={comment}
      currentUserEmail={currentUserEmail}
      projectUuid={projectUuid}
      omitTypes={omitTypes}
      saving={updateComment.isPending}
      deleting={deleteComment.isPending}
      onSave={(content, done) =>
        updateComment.mutate({ commentUuid: comment.uuid, content }, { onSuccess: done })
      }
      onDelete={() => deleteComment.mutate(comment.uuid)}
    />
  )
}

// ---- CommentThreadView: loading, error, empty and the comments ----

export function CommentThreadView<T extends ThreadComment>({
  comments,
  isLoading,
  error,
  emptyLabel = 'No comments yet',
  emptyHint = 'Be the first to add a comment',
  renderComment,
}: {
  comments: T[] | undefined
  isLoading: boolean
  error: unknown
  emptyLabel?: string
  emptyHint?: string | null
  renderComment: (comment: T) => React.ReactNode
}) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-8 w-8 rounded-full shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <Skeleton className="h-3.5 w-24" />
                <Skeleton className="h-3 w-12" />
              </div>
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-3/4" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-destructive">
        Failed to load comments
      </div>
    )
  }

  if (!comments || comments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <MessageSquare className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">{emptyLabel}</p>
        {emptyHint && <p className="text-xs">{emptyHint}</p>}
      </div>
    )
  }

  return <div className="space-y-4">{comments.map(renderComment)}</div>
}

// ---- CommentList ----

export function CommentList({
  projectUuid,
  filterMention,
}: {
  projectUuid: string
  filterMention?: Pick<CommentMention, 'entity_type' | 'entity_uuid'>
}) {
  const { data: comments, isLoading, error } = useProjectComments(projectUuid)
  const { user } = useAuth()
  const visibleComments = filterMention
    ? comments?.filter((comment) =>
        comment.mentions.some(
          (mention) =>
            mention.entity_type === filterMention.entity_type &&
            mention.entity_uuid === filterMention.entity_uuid,
        ),
      )
    : comments

  return (
    <CommentThreadView
      comments={visibleComments}
      isLoading={isLoading}
      error={error}
      emptyLabel={
        filterMention?.entity_type === 'purchase_order' ? 'No comments on this PO yet' : 'No comments yet'
      }
      renderComment={(comment) => (
        <SingleComment
          key={comment.id}
          comment={comment}
          projectUuid={projectUuid}
          currentUserEmail={user?.email}
        />
      )}
    />
  )
}
